import { AgTechSystemAdminRole } from '@agtech/data/users/AgTechUserData'
import { useUserRoles } from '../../App/Auth/AgTechWebAuthContext'
import { AgTechClickEventHandler, useClickable } from '../../Buttons/AgTechButtons'
import React, { createContext, CSSProperties, useContext, useEffect, useRef, useState } from 'react'
import AgTechPageErrorBoundary from '../Error/AgTechPageErrorBoundary'

export type AgTechTabPageProps = {
    tabs: AgTechTabbedPageTabProps[],
    header?: AgTechTabbedPageHeaderProps,
    body?: AgTechTabbedPageBodyConfigurationProps,
    panel?: (tabs: AgTechTabbedPageTabProps[]) => JSX.Element,
    classes?: string,
    styles?: CSSProperties
}

export type AgTechTabbedPageHeaderProps = {
    content?: () => React.ReactNode,
    classes?: string
}

export type AgTechTabbedPageBodyConfigurationProps = {
    classes?: string
}

export type AgTechTabPageTabClasses = {
    container?: string,
    tab?: string,
    inactive?: string,
    active?: string
}

export type AgTechTabbedPageTabProps = {
    text: string,
    displayText?: string, // Used only when the tab text may changed
    content: () => React.ReactNode,
    classes?: AgTechTabPageTabClasses,
    tabStyles?: CSSProperties,
    isEmphasized?: boolean,
    onRightClick?: (e: AgTechClickEventHandler) => void,
    requiredRoles?: string[],
    detailPane?: {
        content: () => React.ReactNode,
        width?: string
    }
}

export type AgTechTabPageContext = {
    currentTab?: AgTechTabbedPageTabProps,
    setCurrentTab: (tab: AgTechTabbedPageTabProps) => void
}

export const AgTechTabPageContext = createContext<AgTechTabPageContext>({
    setCurrentTab: (tab) => { }
});

export const AgTechTabPageTemplate = (props: AgTechTabPageProps) => {
    const userRoles = useUserRoles();
    const [selectedTab, setSelectedTab] = useState(() => {
        return props.tabs[0];
    });

    useEffect(() => {

        const availableTabs = props.tabs.filter(tab => {
            return !tab.requiredRoles || tab.requiredRoles.every(role => userRoles.includes(role));
        });

        // The tabs have changed for this tab page, but we should attempt to stay on the
        // currently selected tab if the new tabs also contain a tab wiht the same text
        let matchingTabs = availableTabs.filter(tab => tab.text === selectedTab?.text);

        if (matchingTabs.length === 1) {
            let matchingTab = matchingTabs[0];
            setSelectedTab(matchingTab);
        }
        else {
            setSelectedTab(availableTabs[0]);
        }

    }, [props.tabs])

    let tabPageContext: AgTechTabPageContext = {
        currentTab: selectedTab,
        setCurrentTab: (tab: AgTechTabbedPageTabProps) => setSelectedTab(tab)
    }

    const availableTabs = props.tabs.filter(tab => {
        return !tab.requiredRoles || tab.requiredRoles.every(role => userRoles.includes(role));
    });

    return (
        <div className='stretched column-based flex'>
            <div className={`stretched row-based flex ${props.classes ?? ''}`} style={props.styles}>
                <AgTechTabPageContext.Provider value={tabPageContext}>
                    {props.panel?.(availableTabs) ?? <AgTechTabbedPageHeader {...props} tabs={availableTabs} />}
                    <AgTechTabbedPageBody
                        currentTab={selectedTab}
                        configuration={props.body}
                    />
                </AgTechTabPageContext.Provider>
            </div>
            {selectedTab?.detailPane && (
                <div className={`tall py-5 pr-6 flex`} style={{ minWidth: selectedTab.detailPane?.width ?? 320 }}>
                    <div className='stretched rounded bordered bg-white flex'>
                        {selectedTab.detailPane.content()}
                    </div>
                </div>
            )}
        </div>
    )
}

const AgTechTabbedPageHeader = (props: AgTechTabPageProps) => {
    return (
        <div className={`wide bottom-bordered pb-3 mb-4 ${props.header?.classes ?? ''}`}>
            <div className="column-based container">
                {props.tabs.map(tab => (
                    <AgTechTabbedPageTab
                        key={tab.displayText ?? tab.text}
                        {...tab}
                    />
                ))}
            </div>
            {props.header?.content && (
                <div className="ml-auto my-auto container">
                    {props.header.content()}
                </div>
            )}
        </div>
    );
}

export const AgTechTabbedPageTab = (props: AgTechTabbedPageTabProps) => {
    const tabButtonRef = useRef(null);
    const tabButtonClickable = useClickable(tabButtonRef);
    
    const tabPageContext: AgTechTabPageContext = useContext<AgTechTabPageContext>(AgTechTabPageContext);
    const isActiveTab = tabPageContext.currentTab?.text === props.text;

    // Handles a right click on the tab
    const handleContextMenuClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (props.onRightClick) {
            e.preventDefault();
            props.onRightClick({
                clickEvent: e,
                ...tabButtonClickable
            });
        }
    }

    const tabClasses = props.classes?.tab ?? `centered border rounded px-4 font-semibold`;
    const tabInactiveClasses = !isActiveTab ? (props.classes?.inactive ?? '') : '';
    const tabActiveClasses = isActiveTab ? (props.classes?.active ?? 'bg-highlight font-color-white') : '';
    const emphasizedClasses = props.isEmphasized ? 'mr-4 right-bordered' : '';
    const tabText = props.requiredRoles?.includes(AgTechSystemAdminRole) ? `${props.displayText ?? props.text} *` : props.displayText ?? props.text;

    const tab = (
        <div 
            className={`clickable ${tabClasses} ${tabInactiveClasses} ${tabActiveClasses} ${emphasizedClasses} container`}
            onClick={() => tabPageContext.setCurrentTab(props)}
            onContextMenu={handleContextMenuClick}
            style={props.tabStyles}
            key={props.displayText ?? props.text} 
            ref={tabButtonRef}
        >
            {tabText}
        </div>
    );

    return props.isEmphasized ? (
        <div className={emphasizedClasses}>
            {tab}
        </div>
    ) : tab;
}

type AgTechTabbedPageBodyProps = {
    currentTab: AgTechTabbedPageTabProps,
    configuration?: AgTechTabbedPageBodyConfigurationProps
}

const AgTechTabbedPageBody = (props: AgTechTabbedPageBodyProps) => {
    const [currentTabName, setCurrentTabName] = useState(props.currentTab?.text);

    useEffect(() => {
        setCurrentTabName(props.currentTab?.text);
    }, [props.currentTab?.text]);

    return (
        <div className={`flexed scrollable flex ${props.configuration?.classes ?? ''}`}>
            <AgTechPageErrorBoundary key={props.currentTab?.text}>
                {currentTabName === props.currentTab?.text ? (
                    <div className='stretched fading-in flex'>
                        {props.currentTab?.content()}
                    </div>
                ) : (
                    <div className='stretched centered flex'>
                        <div className='loader' />
                    </div>
                )}
            </AgTechPageErrorBoundary>
        </div>
    );
}

/// Flavors

const AgTechStandardTabPage = (props: AgTechTabPageProps) => {
    return (
        <AgTechTabPageTemplate
            {...props}
            panel={() => (
                <div className={`wide bottom-bordered pb-3 mb-4 min-h-[42px] column-based container ${props.header?.classes ?? ''}`}>
                    <div className="column-based container">
                        {props.tabs.map(tab => (
                            <AgTechTabbedPageTab
                                key={tab.text}
                                classes={{
                                    tab: 'mr-4 px-4 py-[6px] border-none rounded font-semibold centered w-min-100 flex',
                                    inactive: 'text-secondary'
                                }}
                                {...tab}
                            />
                        ))}
                    </div>
                    {props.header?.content && (
                        <div className="ml-auto vertically-centered container">
                            {props.header.content()}
                        </div>
                    )}
                </div>
            )}
        />
    )
}

export type AgTechTabPageFlavorProps = Omit<AgTechTabPageProps, 'panel'>;

export default AgTechStandardTabPage;